/* Opt-in bridge: native Dawn layout, one visible size, reviewed shipping quantities. */
(() => {
  'use strict';

  const core = window.SCShippingCore;
  const engine = window.SCShippingPrototype?.engine;

  class NativePicker extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.area = this.closest('product-info');
      this.error = this.querySelector('[data-sc-error]');
      this.retry = this.querySelector('[data-sc-native-retry]');
      this.delivery = this.querySelector('[data-sc-delivery]');

      this.retry.addEventListener('click', async () => {
        if (this.busy) return;

        if (!this.config || !this.pairs) {
          return location.reload();
        }

        if (engine?.pending()) {
          this.lock(true);

          try {
            const snapshot = await engine.review();

            await window.SCShippingPrototype.openCart(
              snapshot,
              this.retry
            );

            this.lock(this.personalBlocked());
          } catch (error) {
            this.showError(error);
            this.lock(false);
          }
        } else {
          this.choose();
        }
      });

      Promise.all(
        ['product-info', 'product-form'].map((tag) =>
          customElements.whenDefined(tag)
        )
      )
        .then(() => this.setup())
        .catch((error) => this.showError(error));
    }

    async setup() {
      if (!core || !engine) {
        throw new Error(
          'No se pudo cargar el reparto. Recarga la página.'
        );
      }

      this.form = this.area.querySelector('product-form form');
      this.button = this.area.querySelector(
        'product-form [type="submit"]'
      );
      this.quantity = this.area.querySelector('[name="quantity"]');

      if (!this.form || !this.button) {
        throw new Error(
          'Falta el formulario de compra en esta plantilla.'
        );
      }

      // Intercept the native Dawn submission before product-form handles it.
      // This guarantees that the split engine chooses Express first and uses
      // Standard only for quantities not covered by Express inventory.
      this.onSubmit = (event) => this.submit(event);
      this.form.addEventListener(
        'submit',
        this.onSubmit,
        true
      );

      this.config = JSON.parse(
        this.querySelector('[data-sc-config]').textContent
      );

      if (!this.config.enabled) {
        throw new Error(
          'Este producto no tiene activada la prueba de reparto.'
        );
      }

      this.pairs = core.buildPairs(this.config);

      const initial = this.pairs.find((pair) =>
        [pair.express.id, pair.standard?.id].some(
          (id) => String(id) === this.dataset.selected
        )
      );

      this.visiblePairs = this.pairs.filter(
        (pair) =>
          !pair.stockOnly ||
          pair.express.inventory > 0
      );

      this.version =
        initial && this.visiblePairs.includes(initial)
          ? initial.options[0]
          : this.visiblePairs[0]?.options[0];

      this.size = initial?.options[1];

      this.radios(
        '[data-sc-version-values]',
        [...new Set(
          this.visiblePairs.map((pair) => pair.options[0])
        )],
        this.version,
        'version',
        (value) => {
          this.version = value;
          this.renderSizes();
          this.choose();
        }
      );

      this.renderSizes();
      this.setupDorsal();
      this.setupPatches();

      if (this.quantity) {
        this.quantity.min = this.quantity.dataset.min = '1';
        this.quantity.step = '1';

        this.quantity.addEventListener(
          'change',
          () => this.describe()
        );
      }

      await this.choose();
    }

    setupDorsal() {
      const controls = this.querySelector(
        '[data-sc-dorsal-controls]'
      );

      if (!controls) return;

      this.dorsal = {
        controls,
        plain: this.querySelector('[data-sc-no-dorsal]'),
        personal: this.querySelector('[data-sc-with-dorsal]'),
        name: this.querySelector('[data-sc-dorsal-name]'),
        number: this.querySelector('[data-sc-dorsal-number]'),
        fields: this.querySelector('[data-sc-dorsal-fields]'),
        personalLabel: this.querySelector(
          '[data-sc-with-dorsal-label]'
        ),
        presets: this.querySelector('[data-sc-dorsal-presets]')
      };

      const fixed = this.config.dorsal?.included;

      if (fixed) {
        this.dorsal.personal.checked = true;
        this.dorsal.plain.checked = false;

        this.querySelector(
          '[data-sc-dorsal-title]'
        ).textContent = 'Nombre y número incluidos';

        this.querySelector(
          '[data-sc-dorsal-choices]'
        ).hidden = true;

        this.dorsal.name.value = fixed.name;
        this.dorsal.number.value = fixed.number;

        this.dorsal.name.readOnly =
          this.dorsal.number.readOnly = true;

        this.activePreset = fixed;
      }

      for (const input of [
        this.dorsal.plain,
        this.dorsal.personal
      ]) {
        input.addEventListener('change', () => {
          if (!this.busy) {
            this.syncDorsal();
            this.selectDorsalMedia();
          }
        });
      }

      for (const input of [
        this.dorsal.name,
        this.dorsal.number
      ]) {
        input.addEventListener('input', () => {
          const hadPreset = Boolean(this.activePreset);

          if (!fixed) {
            this.activePreset = null;
          }

          this.syncDorsal();

          if (hadPreset && !fixed) {
            this.selectDorsalMedia();
          }
        });
      }

      if (!fixed) {
        for (
          const value of
          (this.config.dorsal?.presets || [])
        ) {
          const [name, number, mediaAlt] =
            String(value)
              .split('|')
              .map((part) => part.trim());

          if (
            !name ||
            !/^[0-9]{1,3}$/.test(number) ||
            name.length > 18
          ) {
            continue;
          }

          const preset = {
            name,
            number,
            mediaAlt:
              mediaAlt || `${name} ${number}`
          };

          const button =
            document.createElement('button');

          button.type = 'button';
          button.textContent = `${name} ${number}`;

          button.setAttribute(
            'aria-pressed',
            'false'
          );

          button.addEventListener('click', () => {
            if (
              this.busy ||
              this.pair?.stockOnly ||
              !this.config.dorsal?.fee?.available
            ) {
              return;
            }

            this.activePreset = preset;
            this.dorsal.personal.checked = true;
            this.dorsal.plain.checked = false;
            this.dorsal.name.value = name;
            this.dorsal.number.value = number;

            this.syncDorsal();
            this.selectDorsalMedia();
          });

          button.scPreset = preset;
          this.dorsal.presets.append(button);
        }
      }

      this.syncDorsal();
    }

    setupPatches() {
      const controls = this.querySelector(
        '[data-sc-patch-controls]'
      );

      if (!controls) {
        this.patches = null;
        return;
      }

      this.patches = {
        controls,
        without: this.querySelector(
          '[data-sc-without-patches]'
        ),
        with: this.querySelector(
          '[data-sc-with-patches]'
        )
      };

      // The existing patch selection is kept; only the gallery is updated.
      for (const input of [
        this.patches.without,
        this.patches.with
      ]) {
        input?.addEventListener('change', () => {
          if (!this.busy && input.checked) {
            this.selectDorsalMedia();
          }
        });
      }
    }

    patchesSelected() {
      return Boolean(
        this.patches &&
        this.patches.with &&
        this.patches.with.checked
      );
    }

    personalization() {
      const fixed = this.config.dorsal?.included;

      if (fixed) {
        return {
          name: fixed.name,
          number: fixed.number,
          included: true
        };
      }

      if (!this.dorsal?.personal.checked) {
        return null;
      }

      return {
        name:
          this.dorsal.name.value
            .trim()
            .toUpperCase(),
        number:
          this.dorsal.number.value.trim(),
        included: false
      };
    }

    personalBlocked() {
      const selected = this.personalization();

      return Boolean(
        selected &&
        !selected.included &&
        (
          this.pair?.stockOnly ||
          !this.config.dorsal?.fee?.available
        )
      );
    }

    syncDorsal() {
      if (!this.dorsal) return;

      const fixed = this.config.dorsal?.included;
      const selected = this.personalization();

      const available = Boolean(
        fixed ||
        (
          this.config.dorsal?.fee?.available &&
          !this.pair?.stockOnly
        )
      );

      this.dorsal.personal.disabled = !available;
      this.dorsal.fields.hidden = !selected;

      if (this.dorsal.personalLabel) {
        this.dorsal.personalLabel.textContent =
          'Con dorsal';
      }

      this.dorsal.presets
        .querySelectorAll('button')
        .forEach((button) => {
          button.disabled =
            !available || this.busy;

          button.setAttribute(
            'aria-pressed',
            String(
              Boolean(
                selected &&
                button.scPreset === this.activePreset
              )
            )
          );
        });

      this.describe();
      this.updateDorsalPrice();

      this.lock(
        !this.ready ||
        this.busy ||
        this.personalBlocked()
      );
    }

    updateDorsalPrice() {
      const price = this.area?.querySelector(
        `[id="price-${this.area.dataset.section}"]`
      );

      if (!price) return;

      const selected = this.personalization();
      const extra =
        selected &&
        !selected.included &&
        !this.personalBlocked()
          ? 100
          : 0;

      // Only modify the current product price, not a crossed-out
      // comparison price or any other monetary text on the page.
      const amounts = price.querySelectorAll(
        '.price__regular .price-item--regular, ' +
        '.price__sale .price-item--sale, ' +
        '.price__sale .price-item--last'
      );

      for (const amount of amounts) {
        // A fresh node is rendered by choose() for each variant.
        // Preserve its original value to avoid repeated +100 charges
        // when the user toggles the dorsal on and off.
        if (!Object.prototype.hasOwnProperty.call(
          amount.dataset,
          'scBasePriceText'
        )) {
          amount.dataset.scBasePriceText = amount.textContent;
        }

        const baseText = amount.dataset.scBasePriceText;

        if (!extra) {
          amount.textContent = baseText;
          continue;
        }

        const match = baseText.match(/\d[\d.,\s\u00a0]*/);
        if (!match) continue;

        const numberText = match[0].trim().replace(/\s/g, '');
        const separatorIndex = Math.max(
          numberText.lastIndexOf('.'),
          numberText.lastIndexOf(',')
        );
        const decimals =
          separatorIndex !== -1 &&
          numberText.length - separatorIndex - 1 === 2;
        const decimalMark = decimals
          ? numberText[separatorIndex]
          : null;
        const numericDigits = numberText.replace(/\D/g, '');
        const baseValue = Number(numericDigits) / (decimals ? 100 : 1);

        if (!Number.isFinite(baseValue)) continue;

        const updatedValue = (baseValue + extra).toLocaleString(
          decimalMark === ',' ? 'de-DE' : 'en-US',
          {
            minimumFractionDigits: decimals ? 2 : 0,
            maximumFractionDigits: decimals ? 2 : 0
          }
        );

        amount.textContent = baseText.replace(
          match[0],
          match[0].replace(numberText, updatedValue)
        );
      }
    }

    selectPatchesMedia() {
      if (!this.patches?.with || !this.patches?.without) {
        return false;
      }

      // Assign these endings in Shopify's image ALT field:
      // "... - CON PARCHES" and "... - SIN PARCHES".
      const ending = this.patchesSelected()
        ? 'CON PARCHES'
        : 'SIN PARCHES';

      const normalize = (value) =>
        String(value || '')
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/\s+/g, ' ')
          .trim()
          .toUpperCase();

      const gallery = this.area.querySelector('media-gallery');
      const image = [
        ...(gallery?.querySelectorAll(
          '[data-media-id] img[alt]'
        ) || [])
      ].find((img) =>
        normalize(img.alt).endsWith(` - ${ending}`)
      );

      const mediaId = image
        ?.closest('[data-media-id]')
        ?.dataset.mediaId;

      if (!mediaId || !gallery?.setActiveMedia) {
        return false;
      }

      gallery.setActiveMedia(mediaId, true);
      return true;
    }

    selectDorsalMedia() {
      // Patch photos have priority when this product enables the selector.
      // Other products and dorsal presets retain their previous behavior.
      if (this.selectPatchesMedia()) return;

      const selected = this.personalization();
      const preset =
        selected ? this.activePreset : null;

      const normalize = (value) =>
        String(value || '')
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/\s+/g, ' ')
          .trim()
          .toUpperCase();

      const alt =
        preset?.mediaAlt ||
        (
          preset
            ? `${preset.name} ${preset.number}`
            : ''
        );

      const image = alt
        ? [
            ...this.area.querySelectorAll(
              'media-gallery img[alt]'
            )
          ].find(
            (img) =>
              normalize(img.alt) === normalize(alt)
          )
        : null;

      const mediaId =
        image
          ?.closest('[data-media-id]')
          ?.dataset.mediaId ||
        this.defaultMedia;

      if (mediaId) {
        this.area
          .querySelector('media-gallery')
          ?.setActiveMedia(mediaId, true);
      }
    }

    radios(
      selector,
      values,
      selected,
      kind,
      onChange
    ) {
      const holder = this.querySelector(selector);

      holder.replaceChildren();

      values.forEach((value, index) => {
        const input =
          document.createElement('input');

        input.type = 'radio';
        input.name =
          `sc-${kind}-${this.area.dataset.section}`;
        input.id = `${input.name}-${index}`;
        input.value = value;
        input.checked = value === selected;

        const label =
          document.createElement('label');

        label.htmlFor = input.id;
        label.textContent = value;

        input.addEventListener('change', () => {
          if (input.checked) {
            onChange(value);
          }
        });

        holder.append(input, label);
      });
    }

    renderSizes() {
      const values = [
        ...new Set(
          this.visiblePairs
            .filter(
              (pair) =>
                pair.options[0] === this.version
            )
            .map((pair) => pair.options[1])
        )
      ];

      if (!values.includes(this.size)) {
        this.size = values[0];
      }

      this.radios(
        '[data-sc-size-values]',
        values,
        this.size,
        'size',
        (value) => {
          this.size = value;
          this.choose();
        }
      );
    }

    lock(locked) {
      if (!this.button) return;

      this.button.disabled = locked;

      this.button.setAttribute(
        'aria-disabled',
        String(locked)
      );

      this.button.setAttribute(
        'aria-haspopup',
        'dialog'
      );

      this.retry.disabled = locked;
    }

    showError(error) {
      this.error.textContent = error.message;
      this.error.hidden = false;
      this.retry.hidden = false;
    }

    describe() {
      if (!this.pair) {
        this.delivery.textContent =
          'No hay tallas disponibles para esta versión.';

        return;
      }

      const stock = Math.max(
        0,
        this.pair.express.inventory
      );

      const quantity = Number(
        this.quantity?.value || 1
      );

      const personal = this.personalization();

      if (personal && !personal.included) {
        this.delivery.textContent =
          this.personalBlocked()
            ? 'Esta personalización no está disponible para la selección actual.'
            : 'ENVÍO ESTÁNDAR';

        return;
      }

      if (this.pair.stockOnly) {
        this.delivery.textContent =
          stock > 0
            ? 'ENVÍO EXPRESS'
            : 'AGOTADO';

        return;
      }

      if (
        !Number.isSafeInteger(quantity) ||
        quantity < 1
      ) {
        return;
      }

      const {
        express,
        standard
      } = core.allocate(quantity, stock);

      this.delivery.textContent =
        express && standard
          ? 'ENVÍO EXPRESS Y ESTÁNDAR'
          : express
            ? 'ENVÍO EXPRESS'
            : 'ENVÍO ESTÁNDAR';
    }

    async choose() {
      if (this.busy) return;

      this.lock(true);
      this.ready = false;

      this.error.hidden =
        this.retry.hidden = true;

      this.pair = this.pairs.find(
        (pair) =>
          pair.options[0] === this.version &&
          pair.options[1] === this.size
      );

      this.describe();
      this.syncDorsal();

      if (!this.pair) return;

      if (this.quantity) {
        if (this.pair.stockOnly) {
          this.quantity.max =
            this.quantity.dataset.max =
              String(
                Math.max(
                  0,
                  this.pair.express.inventory
                )
              );
        } else {
          this.quantity.removeAttribute('max');
          delete this.quantity.dataset.max;

          this.area
            .querySelector(
              '.quantity__button[name="plus"]'
            )
            ?.classList.remove('disabled');
        }

        this.quantity
          .closest('quantity-input')
          ?.validateQtyRules?.();
      }

      this.controller?.abort();

      const controller =
        this.controller = new AbortController();

      const variantId = this.pair.express.id;

      try {
        const url = new URL(
          this.config.url,
          location.origin
        );

        url.searchParams.set(
          'variant',
          variantId
        );

        url.searchParams.set(
          'section_id',
          this.area.dataset.section
        );

        const response = await fetch(url, {
          credentials: 'same-origin',
          cache: 'no-store',
          signal: controller.signal
        });

        if (!response.ok) {
          throw new Error(
            'No se pudo actualizar la selección. Revisa de nuevo.'
          );
        }

        const html =
          new DOMParser().parseFromString(
            await response.text(),
            'text/html'
          );

        if (
          controller.signal.aborted ||
          !this.isConnected
        ) {
          return;
        }

        const priceId =
          `price-${this.area.dataset.section}`;

        const source =
          html.getElementById(priceId);

        const price =
          this.area.querySelector(
            `[id="${priceId}"]`
          );

        if (!source || !price) {
          throw new Error(
            'No se pudo confirmar el precio. Revisa de nuevo.'
          );
        }

        price.innerHTML = source.innerHTML;

        price
          .querySelector('.price')
          ?.classList.remove('price--sold-out');

        const mediaId =
          html.querySelector(
            '[data-sc-selected-media]'
          )?.dataset.scSelectedMedia;

        this.defaultMedia =
          mediaId
            ? `${this.area.dataset.section}-${mediaId}`
            : null;

        if (mediaId) {
          this.area.updateMedia?.(
            html,
            mediaId
          );
        }

        this.area.updateVariantInputs(
          variantId
        );

        this.area.updateURL(
          this.config.url,
          variantId
        );

        this.area
          .querySelectorAll(
            '.quantity__rules-cart'
          )
          .forEach((node) => {
            node.hidden = true;
          });

        const label =
          this.button.querySelector('span');

        if (label) {
          label.textContent =
            window.variantStrings?.addToCart ||
            'Agregar al carrito';
        }

        this.ready = true;

        this.lock(
          this.personalBlocked()
        );

        this.syncDorsal();
        this.selectDorsalMedia();

        if (engine.pending()) {
          this.showError(
            new Error(
              'Hay un cambio pendiente. Pulsa Revisar de nuevo para recuperar el carrito.'
            )
          );
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          return;
        }

        this.showError(error);
        this.retry.disabled = false;
      }
    }

    async submit(event) {
      event.preventDefault();
      event.stopImmediatePropagation();

      if (
        this.busy ||
        !this.ready ||
        !this.pair
      ) {
        return;
      }

      if (this.personalBlocked()) {
        return this.showError(
          new Error(
            'Esta personalización no está disponible para la selección actual.'
          )
        );
      }

      const quantity = Number(
        this.quantity?.value || 1
      );

      if (
        !Number.isSafeInteger(quantity) ||
        quantity < 1
      ) {
        return this.showError(
          new Error(
            'Introduce una cantidad entera mayor que cero.'
          )
        );
      }

      const data = new FormData(this.form);

      if (
        [...data.keys()].some(
          (key) =>
            key.startsWith('properties[') ||
            key === 'selling_plan'
        )
      ) {
        return this.showError(
          new Error(
            'No se admiten campos externos de personalización ni suscripciones en este formulario.'
          )
        );
      }

      const personal =
        this.personalization();

      const patches =
        this.patchesSelected();

      if (personal) {
        try {
          core.personalPair(
            this.pair,
            personal
          );
        } catch (error) {
          this.showError(error);
          return;
        }
      }

      this.busy = true;
      this.lock(true);

      // Prevent changing the selected pair while the cart operation is in flight.
      this.querySelectorAll('input').forEach(
        (input) => {
          input.disabled = true;
        }
      );

      try {
        const snapshot = await engine.add(
          this.config.url,
          this.pair.id,
          quantity,
          personal,
          patches
        );

        await window.SCShippingPrototype.openCart(
          snapshot,
          this.button
        );
      } catch (error) {
        this.busy = false;
        this.lock(false);

        this.querySelectorAll('input').forEach(
          (input) => {
            input.disabled = false;
          }
        );

        this.showError(error);
      } finally {
        this.busy = false;

        this.querySelectorAll('input').forEach(
          (input) => {
            input.disabled = false;
          }
        );

        if (
          this.config.dorsal?.included &&
          this.dorsal
        ) {
          this.dorsal.name.readOnly =
            this.dorsal.number.readOnly = true;
        }

        this.lock(
          !this.ready ||
          this.personalBlocked()
        );

        this.syncDorsal();
      }
    }

    disconnectedCallback() {
      this.controller?.abort();

      if (this.form && this.onSubmit) {
        this.form.removeEventListener(
          'submit',
          this.onSubmit,
          true
        );
      }
    }
  }

  customElements.define(
    'sc-native-picker',
    NativePicker
  );
})();
